import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useThunk } from "../hooks/useThunk";
import { getUserProfile } from "../store";
import showToast from "../utils/toastNotifications";
import { clearUser } from "../store";
import { useDispatch } from "react-redux";
import { clearSelectedThreadRoomIdAndThreadId } from "../store";
import { api } from "../config/api";

const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    return !! token;
};

const guestOnlyPaths = new Set(["/signin", "/signup"]);

export const ProtectedRoute = () => {

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const locations = useLocation();
    const [doFetchUserProfile] = useThunk(getUserProfile);

    useEffect(() => {
        if (isAuthenticated()) {
            doFetchUserProfile();

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                api.post("/user/timezone", { timezone }).catch(() => {
                    // Timezone syncing must never interrupt access to the dashboard.
                });
            }
        }
    }, [doFetchUserProfile]);

    useEffect(() => {
        if(!locations.pathname?.startsWith("/thread")){
            dispatch(clearSelectedThreadRoomIdAndThreadId());
        }
    }, [dispatch, locations.pathname]);

    useEffect(() => {
        const handleStorageChange = (e) => {
            console.log("inside the handler")
            if(e.key === "token" && e.newValue === null ){
                dispatch(clearUser());
                showToast("error", "Session Expired. Please login Again")
                navigate('/signin', {replace: true})
            }
        } 
        window.addEventListener("storage", handleStorageChange)
        return () => {
            window.removeEventListener("storage", handleStorageChange)
        }
    },[])

    // Retain only course payment returns through password, OTP or social sign-in.
    // The stored value can never select an external destination.
    const isCourseReturn = /^\/courses\/COURSE-[1-9]\d{4,}\/payment-return$/.test(locations.pathname);
    useEffect(() => {
        if (!isAuthenticated() && isCourseReturn) {
            sessionStorage.setItem("coursePaymentReturn", locations.pathname + locations.search);
        }
    }, [isCourseReturn, locations.pathname, locations.search]);
    useEffect(() => {
        if (isAuthenticated() && locations.pathname === "/") {
            const pending = sessionStorage.getItem("coursePaymentReturn");
            sessionStorage.removeItem("coursePaymentReturn");
            if (pending && /^\/courses\/COURSE-[1-9]\d{4,}\/payment-return\?[^#]*$/.test(pending)) { sessionStorage.removeItem("courseMessageReturn"); navigate(pending, { replace: true }); }
        }
    }, [locations.pathname, navigate]);
    // Course email links stay local and survive password, OTP and social login.
    useEffect(() => {
        if (!isAuthenticated() && /^\/courses\/COURSE-[1-9]\d{4,}\/messages$/.test(locations.pathname)) {
            sessionStorage.setItem("courseMessageReturn", locations.pathname);
        }
        if (isAuthenticated() && locations.pathname === "/" && !sessionStorage.getItem("coursePaymentReturn")) {
            const pending = sessionStorage.getItem("courseMessageReturn");
            sessionStorage.removeItem("courseMessageReturn");
            if (pending && /^\/courses\/COURSE-[1-9]\d{4,}\/messages$/.test(pending)) navigate(pending, { replace: true });
        }
    }, [locations.pathname, navigate]);
    return isAuthenticated() ? (
        <Outlet />
    ) : <Navigate to="/signin" replace />
};

export const PublicRoute = () => {

    const locations = useLocation();
    const dispatch = useDispatch();
    const [authenticated, setAuthenticated] = useState(isAuthenticated);

    useEffect(() => {
        if(!locations.pathname?.startsWith("/thread")){
            dispatch(clearSelectedThreadRoomIdAndThreadId());
        }
    }, [dispatch, locations.pathname]);

    useEffect(() => {
        setAuthenticated(isAuthenticated());
    }, [locations.pathname]);

    useEffect(() => {
        const syncAuthentication = () => setAuthenticated(isAuthenticated());
        const handleStorageChange = (event) => {
            if (event.key === "token" || event.key === null) {
                syncAuthentication();
            }
        };

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("focus", syncAuthentication);
        window.addEventListener("pageshow", syncAuthentication);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("focus", syncAuthentication);
            window.removeEventListener("pageshow", syncAuthentication);
        };
    }, []);

    const normalizedPath = locations.pathname.replace(/\/+$/, "") || "/";
    return authenticated && guestOnlyPaths.has(normalizedPath)
        ? <Navigate to="/dashboard" replace />
        : <Outlet />;
};

export const OtpRouteGuard = () => {

    const location = useLocation();
    const otpAvailable= location.state?.otpAvailable;
    return !otpAvailable ? <Navigate to="/" replace/> : <Outlet/>
}
